// Register page component - Clean template design
import { router } from '@/utils/router'
import { api } from '@/services/api'
import { validateNIF, validateEmail } from '@/utils/validation'

interface PartnerOption {
  id: string | null
  nome: string
}

export class RegisterPage {
  private container: HTMLElement | null = null
  private partners: PartnerOption[] = []

  async render(): Promise<HTMLElement> {
    this.container = document.createElement('div')
    this.container.innerHTML = `
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .register-body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          background-color: #f5f5f5;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 20px;
        }

        .register-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 40px;
          width: 100%;
          max-width: 420px;
        }

        .header {
          text-align: center;
          margin-bottom: 32px;
        }

        .header h1 {
          font-size: 28px;
          font-weight: bold !important;
          color: #000000 !important;
          margin-bottom: 8px;
        }

        .header p {
          color: #666;
          font-size: 16px;
          line-height: 1.4;
        }

        .tab-container {
          display: flex;
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 4px;
          margin-bottom: 32px;
        }

        .tab {
          flex: 1;
          padding: 12px 20px;
          text-align: center;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          border: none;
          background: none;
        }

        .tab.active {
          background-color: white;
          color: #1a1a1a;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .tab:not(.active) {
          color: #666;
        }

        .tab:not(.active):hover {
          color: #1a1a1a;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          color: #1a1a1a;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          background-color: white;
        }

        .form-group input::placeholder {
          color: #999;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
        }

        .register-btn {
          width: 100%;
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 14px 20px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-bottom: 24px;
        }

        .register-btn:hover:not(:disabled) {
          background-color: #3367d6;
        }

        .register-btn:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .back-link {
          text-align: center;
        }

        .back-link button {
          color: #666;
          background: none;
          border: none;
          font-size: 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: color 0.2s ease;
          cursor: pointer;
        }

        .back-link button:hover {
          color: #4285f4;
        }

        .back-arrow {
          font-size: 12px;
        }

        .message {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          font-size: 14px;
          font-weight: 500;
        }

        .message.error {
          background: #fee;
          border: 1px solid #fcc;
          color: #c33;
        }

        .message.success {
          background: #efe;
          border: 1px solid #cfc;
          color: #3c3;
        }

        @media (max-width: 480px) {
          .register-container {
            padding: 30px 20px;
            margin: 10px;
          }
          
          .header h1 {
            font-size: 24px;
            font-weight: bold !important;
            color: #000000 !important;
          }
        }
      </style>

      <div class="register-body">
        <div class="register-container">
          <div class="header">
            <h1>Criar conta</h1>
            <p>Junte-se para começar a construir os seus projectos.</p>
          </div>

          <div class="tab-container">
            <button class="tab" data-tab="login">Iniciar sessão</button>
            <button class="tab active" data-tab="register">Criar conta</button>
          </div>

          <form id="registerForm">
            <div class="form-group">
              <label for="name">O seu nome completo</label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                placeholder="O seu nome completo"
                required
              />
            </div>

            <div class="form-group">
              <label for="company_name">Nome da sua empresa (opcional)</label>
              <input 
                type="text" 
                id="company_name" 
                name="company_name" 
                placeholder="Nome da sua empresa"
              />
            </div>

            <div class="form-group">
              <label for="nif">NIF (9 dígitos)</label>
              <input 
                type="text" 
                id="nif" 
                name="nif" 
                placeholder="123456789"
                maxlength="9"
                pattern="[0-9]{9}"
                required
              />
            </div>

            <div class="form-group">
              <label for="email">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="seu@email.com"
                required
              />
            </div>

            <div class="form-group">
              <label for="partner_id">Serviço de Contabilidade</label>
              <select id="partner_id" name="partner_id">
                <option value="">A carregar...</option>
              </select>
              <small style="color: #666; font-size: 12px; margin-top: 4px; display: block;">
                Selecione o seu serviço de contabilidade ou escolha "Nenhum / Não aplicável" se não tiver.
              </small>
            </div>

            <div class="form-group">
              <label for="password">Palavra-passe (mínimo 8 caracteres)</label>
              <input 
                type="password" 
                id="password" 
                name="password" 
                placeholder="Mínimo 8 caracteres"
                required
              />
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirmar palavra-passe</label>
              <input 
                type="password" 
                id="confirmPassword" 
                name="confirmPassword" 
                placeholder="Repita a sua palavra-passe"
                required
              />
            </div>

            <button type="submit" class="register-btn" id="submitBtn">
              Criar conta
            </button>
          </form>

          <div class="back-link">
            <button type="button" id="backLink">
              <span class="back-arrow">←</span>
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    `

    await this.loadPartners()
    this.setupEventListeners()
    this.autoFillFromUrlParams()
    return this.container
  }

  private async loadPartners(): Promise<void> {
    try {
      const response = await api.get<{ partners: PartnerOption[] }>('/api/partners/active')
      this.partners = response.partners
      this.populatePartnersDropdown()
    } catch (error) {
      console.error('Failed to load partners:', error)
      this.showMessage('Erro ao carregar lista de serviços de contabilidade', 'error')
      // Set a fallback N/A option
      this.partners = [{ id: null, nome: 'Nenhum / Não aplicável' }]
      this.populatePartnersDropdown()
    }
  }

  private populatePartnersDropdown(): void {
    if (!this.container) return

    const partnerSelect = this.container.querySelector('#partner_id') as HTMLSelectElement
    if (!partnerSelect) return

    // Clear all existing options
    partnerSelect.innerHTML = ''

    // Add partner options (N/A option should come from API as first item)
    this.partners.forEach(partner => {
      const option = document.createElement('option')
      option.value = partner.id ?? '' // Empty string for null id
      option.textContent = partner.nome
      partnerSelect.appendChild(option)
    })
  }

  private setupEventListeners(): void {
    if (!this.container) return

    const form = this.container.querySelector('#registerForm') as HTMLFormElement
    const backLink = this.container.querySelector('#backLink') as HTMLButtonElement
    const loginTab = this.container.querySelector('[data-tab="login"]') as HTMLButtonElement

    // Form inputs
    const nameInput = this.container.querySelector('#name') as HTMLInputElement
    const companyInput = this.container.querySelector('#company_name') as HTMLInputElement
    const nifInput = this.container.querySelector('#nif') as HTMLInputElement
    const emailInput = this.container.querySelector('#email') as HTMLInputElement
    const partnerInput = this.container.querySelector('#partner_id') as HTMLSelectElement
    const passwordInput = this.container.querySelector('#password') as HTMLInputElement
    const confirmPasswordInput = this.container.querySelector('#confirmPassword') as HTMLInputElement

    // Form validation and submission
    form.addEventListener('submit', this.handleSubmit.bind(this))

    // Input validation
    nameInput.addEventListener('input', () => this.validateForm())
    companyInput.addEventListener('input', () => this.validateForm())
    nifInput.addEventListener('input', (e) => {
      const input = e.target as HTMLInputElement
      input.value = input.value.replace(/\D/g, '') // Only numbers
      this.validateForm()
    })
    emailInput.addEventListener('input', () => this.validateForm())
    passwordInput.addEventListener('input', () => this.validateForm())
    confirmPasswordInput.addEventListener('input', () => this.validateForm())
    partnerInput.addEventListener('change', () => this.validateForm())

    // Tab navigation
    loginTab.addEventListener('click', () => {
      router.navigate('/login')
    })

    // Back button
    backLink.addEventListener('click', () => {
      router.navigate('/')
    })

    // Initial validation
    this.validateForm()
  }

  private validateForm(): void {
    if (!this.container) return

    const nameInput = this.container.querySelector('#name') as HTMLInputElement
    const nifInput = this.container.querySelector('#nif') as HTMLInputElement
    const emailInput = this.container.querySelector('#email') as HTMLInputElement
    const passwordInput = this.container.querySelector('#password') as HTMLInputElement
    const confirmPasswordInput = this.container.querySelector('#confirmPassword') as HTMLInputElement
    const submitBtn = this.container.querySelector('#submitBtn') as HTMLButtonElement

    const name = nameInput.value.trim()
    const nif = nifInput.value.trim()
    const email = emailInput.value.trim()
    const password = passwordInput.value.trim()
    const confirmPassword = confirmPasswordInput.value.trim()

    // Partner is optional (N/A is a valid selection)
    const isValid = name.length > 0 &&
      nif.length === 9 &&
      this.isValidEmail(email) &&
      password.length >= 8 &&
      password === confirmPassword

    submitBtn.disabled = !isValid
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault()

    if (!this.container) return

    const nameInput = this.container.querySelector('#name') as HTMLInputElement
    const companyInput = this.container.querySelector('#company_name') as HTMLInputElement
    const nifInput = this.container.querySelector('#nif') as HTMLInputElement
    const emailInput = this.container.querySelector('#email') as HTMLInputElement
    const passwordInput = this.container.querySelector('#password') as HTMLInputElement
    const confirmPasswordInput = this.container.querySelector('#confirmPassword') as HTMLInputElement
    const partnerInput = this.container.querySelector('#partner_id') as HTMLSelectElement
    const submitBtn = this.container.querySelector('#submitBtn') as HTMLButtonElement

    // Get partner_id value (empty string means N/A / null)
    const partnerValue = partnerInput.value
    const partnerId = partnerValue === '' ? null : partnerValue

    // Collect form data
    const formData = {
      name: nameInput.value.trim(),
      company_name: companyInput.value.trim() || undefined,
      nif: nifInput.value.trim(),
      email: emailInput.value.trim(),
      password: passwordInput.value.trim(),
      confirmPassword: confirmPasswordInput.value.trim(),
      partner_id: partnerId,
    }

    // Validation
    if (!formData.name || !formData.nif || !formData.email ||
      !formData.password || !formData.confirmPassword) {
      this.showMessage('Por favor, preencha todos os campos obrigatórios', 'error')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      this.showMessage('As palavras-passe não coincidem', 'error')
      return
    }

    if (formData.nif.length !== 9 || !validateNIF(formData.nif)) {
      this.showMessage('NIF inválido', 'error')
      return
    }

    if (!validateEmail(formData.email)) {
      this.showMessage('Email inválido', 'error')
      return
    }

    if (formData.password.length < 8) {
      this.showMessage('A palavra-passe deve ter pelo menos 8 caracteres', 'error')
      return
    }

    // Show loading state
    submitBtn.disabled = true
    submitBtn.textContent = 'A criar conta...'

    try {
      // Transform data to match the backend API format
      const registerData: Record<string, unknown> = {
        name: formData.name,
        company_name: formData.company_name,
        email: formData.email,
        password: formData.password,
        nif: formData.nif,
        vehicle_type: 'CAR' // Hardcoded as specified in the API
      }

      // Only include partner_id if it's not null (N/A selection)
      if (formData.partner_id) {
        registerData.partner_id = formData.partner_id
      }

      const response = await api.post('/api/v1/auth/register', registerData)
      this.storeAuthData(response)
      this.showMessage('Conta criada com sucesso!', 'success')

      setTimeout(() => {
        // Check if there's a subscription intent or return URL
        const urlParams = new URLSearchParams(window.location.search)
        const returnUrl = urlParams.get('return')
        const action = urlParams.get('action')

        if (returnUrl === 'pricing' && action === 'subscribe') {
          router.navigate('/pricing')
        } else {
          router.navigate('/dashboard')
        }
      }, 2000)
    } catch (error: any) {
      console.error('Registration error:', error)
      let errorMessage = 'Erro durante o registo. Tente novamente.'

      // Handle API response errors with specific error codes
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error instanceof Error) {
        if (error.message.includes('NIF already registered') ||
          error.message.includes('NIF já se encontra registado')) {
          errorMessage = 'Este NIF já se encontra registado. Tente iniciar sessão.'
        } else if (error.message.includes('email já se encontra registado') ||
          error.message.includes('email already registered')) {
          errorMessage = 'Este email já se encontra registado. Tente usar outro email.'
        } else if (error.message.includes('Invalid Portuguese NIF')) {
          errorMessage = 'NIF português inválido'
        } else {
          errorMessage = error.message
        }
      }

      this.showMessage(errorMessage, 'error')
    } finally {
      // Hide loading state
      submitBtn.disabled = false
      submitBtn.textContent = 'Criar conta'
    }
  }

  private storeAuthData(data: any): void {
    if (data.token) {
      localStorage.setItem('auth_token', data.token)
    }

    if (data.user) {
      localStorage.setItem('user_data', JSON.stringify(data.user))
    }

    localStorage.setItem('login_timestamp', new Date().toISOString())
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    // Remove any existing message
    const existingMessage = document.querySelector('.message')
    if (existingMessage) {
      existingMessage.remove()
    }

    const messageElement = document.createElement('div')
    messageElement.className = `message ${type}`
    messageElement.textContent = message

    document.body.appendChild(messageElement)

    setTimeout(() => {
      messageElement.remove()
    }, 5000)
  }

  private autoFillFromUrlParams(): void {
    if (!this.container) return

    const urlParams = new URLSearchParams(window.location.search)
    const nifParam = urlParams.get('nif')

    if (nifParam) {
      const nifInput = this.container.querySelector('#nif') as HTMLInputElement
      if (nifInput) {
        nifInput.value = nifParam
        // Trigger validation after auto-filling
        this.validateForm()
      }
    }
  }
}
